@if (subjectData(); as s) {
  <div class="client-metadata">
    <p-accordion>
      <p-accordion-panel value="1">
        <p-accordion-header>
          <div class="bai-meta-header-content">
            @if (s.isEligible) {
              <i class="pi pi-user text-green-500 mr-2"
                 pTooltip="This client is eligible for Brightside services"
                 tooltipPosition="bottom">
              </i>
            } @else {
              <i class="pi pi-user text-red-500 mr-2"
                 pTooltip="This client is not eligible for Brightside services"
                 tooltipPosition="bottom">
              </i>
            }

            <span class="bai-meta-name">{{ s.firstName }} {{ s.lastName }}</span>
            <span class="bai-meta-pronouns" *ngIf="inferredPronouns()"> ({{ inferredPronouns() }})</span>

            <ng-container *ngIf="s.company">
              <span class="bai-meta-sep" aria-hidden="true">|</span>
              <span class="bai-meta-field">{{ s.company | capitalizeFirst }}</span>
            </ng-container>
            <ng-container *ngIf="s.employmentType">
              <span class="bai-meta-sep" aria-hidden="true">|</span>
              <span class="bai-meta-field">{{ s.employmentType | capitalizeFirst }}</span>
            </ng-container>
            <ng-container *ngIf="s.gender">
              <span class="bai-meta-sep" aria-hidden="true">|</span>
              <span class="bai-meta-field">{{ s.gender }}</span>
            </ng-container>
            <ng-container *ngIf="age()">
              <span class="bai-meta-sep" aria-hidden="true">|</span>
              <span class="bai-meta-field">Age {{ age() }}</span>
            </ng-container>
            <ng-container *ngIf="s.hireDate">
              <span class="bai-meta-sep" aria-hidden="true">|</span>
              <span class="bai-meta-field">{{ tenureString() }}</span>
            </ng-container>
            <ng-container *ngIf="s.assignedFaName">
              <span class="bai-meta-sep" aria-hidden="true">|</span>
              <span class="bai-meta-field">FA: {{ s.assignedFaName }}</span>
            </ng-container>

            <!-- Quick action buttons — always visible, stop accordion toggle -->
            <span class="bai-meta-actions" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
              <button
                type="button"
                class="bai-meta-action-btn"
                [attr.aria-label]="'Call ' + s.firstName + ' ' + s.lastName"
                pTooltip="Call client"
                tooltipPosition="bottom"
                (click)="callClient($event)"
              >
                <i class="pi pi-phone"></i>
              </button>
              <button
                type="button"
                class="bai-meta-action-btn"
                [attr.aria-label]="'Email ' + s.firstName + ' ' + s.lastName"
                pTooltip="Email client"
                tooltipPosition="bottom"
                (click)="emailClient($event)"
              >
                <i class="pi pi-envelope"></i>
              </button>
              <!-- More menu trigger only — dropdown is rendered outside the accordion -->
              <button
                type="button"
                class="bai-meta-action-btn"
                aria-label="More actions"
                aria-haspopup="true"
                [attr.aria-expanded]="showMoreMenu()"
                (click)="toggleMoreMenu($event)"
              >
                <i class="pi pi-bars"></i>
              </button>
            </span>
          </div>
        </p-accordion-header>

        <p-accordion-content>
          <div class="flex flex-col lg:flex-row gap-6 pt-4 text-sm">
            <!-- Metadata Section -->
            <div class="flex-1 space-y-3">

              <!-- Assigned FA -->
              <div class="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2">
                <div>
                  <span class="text-gray-500 font-bold">Assigned FA:</span>
                  {{ assignedFa() }}
                </div>
              </div>

              <!-- GUID row -->
              <div class="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-gray-500 font-bold">GUID:</span>
                  @if (s.guid) {
                    <span class="font-mono break-all text-gray-700 text-xs">{{ s.guid }}</span>
                    <button
                      pButton
                      type="button"
                      icon="pi pi-copy"
                      class="p-button-sm p-button-text py-0 h-6"
                      pTooltip="Copy GUID"
                      tooltipPosition="top"
                      (click)="copyToClipboard(s.guid)">
                    </button>
                  } @else {
                    <span class="text-gray-700">N/A</span>
                  }
                </div>
              </div>

              <!-- Interaction dates + Location + Dependents -->
              <div class="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2">
                <div>
                  <span class="text-gray-500 font-bold">First Interaction:</span>
                  {{ firstInteraction() }}
                </div>
                <div>
                  <span class="text-gray-500 font-bold">Most Recent Interaction:</span>
                  {{ mostRecentInteraction() }}
                </div>
                <div>
                  <span class="text-gray-500 font-bold">Location:</span>
                  {{ s.city || 'N/A' }}, {{ s.state || 'N/A' }}
                </div>
                <div>
                  <span class="text-gray-500 font-bold">Household Dependents:</span>
                  @if (householdDependentNames(); as names) {
                    {{ names.join(', ') }}
                  } @else {
                    —
                  }
                </div>
              </div>

              <!-- Account balances (conditional) -->
              @if (spendingBalance() !== null || savingsBalance() !== null) {
                <div class="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2">
                  @if (spendingBalance() !== null) {
                    <div>
                      <span class="text-gray-500 font-bold">Spending Account:</span>
                      {{ formatUsd(spendingBalance()!) }}
                    </div>
                  }
                  @if (savingsBalance() !== null) {
                    <div>
                      <span class="text-gray-500 font-bold">Savings Account:</span>
                      {{ formatUsd(savingsBalance()!) }}
                    </div>
                  }
                </div>
              }

            </div>

            <!-- Client Links Section (external links only) -->
            @if (s.links.length) {
              <div class="border-t lg:border-t-0 lg:border-l lg:pl-6 border-gray-300 flex flex-col gap-2 pt-4 lg:pt-0 min-w-[200px]">
                <div class="text-gray-500 font-bold mb-1">Client Links:</div>
                @for (link of s.links; track link.label) {
                  <button
                    type="button"
                    class="flex items-center gap-2 rounded p-1"
                    [class.text-blue-600]="link.url"
                    [class.hover:underline]="link.url"
                    [class.opacity-50]="!link.url"
                    [disabled]="!link.url"
                    (click)="link.url && openLink(link.url)"
                  >
                    <img
                      [src]="'assets/brightai/icons/' + link.label.toLowerCase() + '_icon.svg'"
                      [alt]="link.label + ' icon'"
                      class="w-6 h-6"
                    />
                  </button>
                }
              </div>
            }
          </div>
        </p-accordion-content>

      </p-accordion-panel>
    </p-accordion>
  </div>

  <!-- More dropdown — rendered outside the accordion to avoid overflow:hidden clipping -->
  @if (showMoreMenu()) {
    <div class="bai-meta-more-dropdown" role="menu" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
      <button
        type="button"
        role="menuitem"
        class="bai-meta-more-item"
        (click)="openNoteModal($event)"
      >
        <i class="pi pi-pencil"></i> Add Note
      </button>
    </div>
  }

  <!-- ======================================================================
       Add Note Modal
  ====================================================================== -->
  @if (noteModalOpen()) {
    <div
      class="bai-meta-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Add advisor note"
      (click)="closeNoteModal()"
      (keydown.escape)="closeNoteModal()"
      tabindex="-1"
    >
      <div class="bai-meta-modal" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
        <div class="bai-meta-modal__header">
          <h3 class="bai-meta-modal__title"><i class="pi pi-pencil"></i> Add Advisor Note</h3>
          <button
            type="button"
            class="bai-meta-modal__close"
            (click)="closeNoteModal()"
            aria-label="Close note modal"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="bai-meta-modal__body">
          <div class="bai-meta-modal__field">
            <label class="bai-meta-modal__label" for="meta-note-title">
              Title <span class="bai-meta-modal__optional">(optional)</span>
            </label>
            <input
              id="meta-note-title"
              type="text"
              class="bai-meta-modal__input"
              placeholder="Note title…"
              [ngModel]="noteTitle()"
              (ngModelChange)="noteTitle.set($event)"
            />
          </div>

          <div class="bai-meta-modal__field">
            <label class="bai-meta-modal__label" for="meta-note-body">
              Note <span class="bai-meta-modal__required">*</span>
            </label>
            <textarea
              id="meta-note-body"
              class="bai-meta-modal__textarea"
              rows="5"
              placeholder="Write your note here…"
              [ngModel]="noteBody()"
              (ngModelChange)="noteBody.set($event)"
            ></textarea>
          </div>

          <div class="bai-meta-modal__field">
            <label class="bai-meta-modal__label" for="meta-note-timestamp">Timestamp</label>
            <input
              id="meta-note-timestamp"
              type="datetime-local"
              class="bai-meta-modal__input"
              [ngModel]="noteTimestamp()"
              (ngModelChange)="noteTimestamp.set($event)"
            />
          </div>
        </div>

        <div class="bai-meta-modal__footer">
          <button type="button" class="bai-meta-btn bai-meta-btn--ghost" (click)="closeNoteModal()">
            Cancel
          </button>
          <button
            type="button"
            class="bai-meta-btn bai-meta-btn--primary"
            [disabled]="!noteCanSubmit()"
            (click)="submitNote()"
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  }
}
